import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import type { Form as FactorialForm } from 'factorial-form'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type Survey from 'modules/surveys/store/models/v2/Survey'

interface ChangeEndDateSingleSurveyModalProps {
  form: FactorialForm
  survey: Survey
  onClose: () => void
  onSubmit: () => void
  running: boolean
  isSingleSurveySelected: boolean
}

const ChangeEndDateSurveyModal = ({
  form,
  survey,
  onClose,
  onSubmit,
  running,
  isSingleSurveySelected,
}: ChangeEndDateSingleSurveyModalProps) => {
  const now = moment.utc()
  const currentStartDateTime = survey.startOnUtcDateTime
  const newDaysToAnswer = form.get('duration_in_days').value
  const hasNewEndDateSet = newDaysToAnswer !== ''
  const currentEndDate = moment(currentStartDateTime)
    .add(survey.attributes.duration_in_days, 'days')
    .local()
  const newEndDate =
    hasNewEndDateSet && moment(currentStartDateTime).add(newDaysToAnswer, 'days').local()

  const actionNotAllowed = hasNewEndDateSet && newEndDate < now
  const endDateWillBeExtended = newEndDate > currentEndDate

  const newEndDateFormatted = moment(currentStartDateTime).add(newDaysToAnswer, 'days').local()

  const endDateExplained = endDateWillBeExtended
    ? i18n.t('surveys.form.settings.cycle_configuration.modals.change_end_date.end_date_extended')
    : i18n.t('surveys.form.settings.cycle_configuration.modals.change_end_date.end_date_shortened')

  const endDateExplainedModal = {
    end_date_explained: endDateExplained,
    new_end_date: dateToString(newEndDateFormatted, 'ui'),
  }

  const title = hasNewEndDateSet
    ? i18n.t(
        'surveys.form.settings.cycle_configuration.modals.change_end_date.title',
        endDateExplainedModal
      )
    : isSingleSurveySelected
      ? i18n.t('surveys.form.settings.cycle_configuration.modals.no_end_date.title')
      : i18n.t(
          'surveys.form.settings.cycle_configuration.modals.no_end_date.title_reoccurring_survey'
        )

  const description = hasNewEndDateSet
    ? i18n.t(
        'surveys.form.settings.cycle_configuration.modals.change_end_date.description',
        endDateExplainedModal
      )
    : isSingleSurveySelected
      ? i18n.t('surveys.form.settings.cycle_configuration.modals.no_end_date.description')
      : i18n.t(
          'surveys.form.settings.cycle_configuration.modals.no_end_date.description_reoccurring_survey'
        )

  if (actionNotAllowed)
    return (
      <Modal
        illustration="alert"
        title={i18n.t('surveys.form.settings.cycle_configuration.modals.action_not_allowed.title')}
        description={i18n.t(
          'surveys.form.settings.cycle_configuration.modals.action_not_allowed.description'
        )}
        onClose={onClose}
        footer={
          <Button type="primary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
        }
      />
    )

  return (
    <Modal
      illustration="alert"
      title={title}
      description={description}
      onClose={onClose}
      onSubmit={(closePortal: ClosePortalFn) => {
        onSubmit()
        closePortal()
      }}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={running} submit>
            {i18n.t('common.continue')}
          </Button>
        </>
      }
    />
  )
}

export default observer(ChangeEndDateSurveyModal)
